import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { getBuffPrice, getRowImage, buildStickerIndex, withStickerImages } from './helpers';
import StickerSlots from './StickerSlots';
import {
  ZONE, fmt, drop,
  ZoneHead, ZoneStats, ZoneSearch, ZoneChips, ZoneEmpty, ZoneFooter,
} from './zoneShared';
import { openLends, closedLends, daysOut, daysUntilDue, isOverdue, lentValue } from './lending';
import { rowName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal from './ItemDetailModal';

// Lending Zone — "THE LOAN BOARD": the lending page on the Overview 7 board.
// Same data and same actions as the classic tab, hard edges instead of glass.
const FILTERS = [
  { key: 'open',     label: 'Out now' },
  { key: 'overdue',  label: 'Overdue' },
  { key: 'returned', label: 'Came back' },
];

const fmtDate = iso => iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : 'n/a';

// Same loan, same key, in both the pricing pass and the render pass. See the
// note in LendingTab.jsx — the render walks a filtered list, so its index is
// not the one the request list used.
const lendKey = e => e.lend.id || `${e.realIndex}_${e.lend.at}`;

export default function LendingTabZone({
  rows, skinsData = [], livePrices, csfloatPrices, inventoryMap, syncedInventory = [],
  onLendItems, onMarkReturned, onWriteOff,
}) {
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('open');
  const [detail, setDetail] = useState(null);

  const open = useMemo(
    () => openLends({ rows, inventoryItems: syncedInventory }),
    [rows, syncedInventory],
  );
  const closed = useMemo(() => closedLends(rows), [rows]);

  // Rows rebuilt from the trade history carry sticker names but no pictures.
  // Built once here rather than per card, since the database is 40k entries.
  const stickerIndex = useMemo(() => buildStickerIndex(skinsData), [skinsData]);

  // What the market did over the life of each loan, priced from the CSFloat
  // archive on the day the item actually left — see LendingTab.jsx for why the
  // row's own snapshots could not answer this.
  const lendPoints = useMemo(() => [...open, ...closed].map(e => ({
    key:  lendKey(e),
    name: rowName(e.row),
    date: dayOf(e.lend.at),
    to:   dayOf(e.lend.returnedAt || e.lend.writtenOffAt),
  })), [open, closed]);
  const archive = useSinceMoves(lendPoints, csfloatPrices);

  const totals = useMemo(() => {
    const value = open.reduce((s, e) => s + (getBuffPrice(livePrices, e.row) ?? parseFloat(e.row.buyPrice) ?? 0), 0);
    const people = new Set(open.map(e => e.lend.to.steamId64 || e.lend.to.name.toLowerCase()));

    // Drift over every loan that can be priced at both ends. Left out entirely
    // when it cannot be, rather than counted at zero.
    const drift = open.reduce((acc, e) => {
      const mv = archive.get(lendKey(e));
      const then = mv ? mv.then : lentValue(e.row, e.lend);
      const now  = mv ? mv.now  : getBuffPrice(livePrices, e.row);
      if (then == null || now == null) return acc;
      return { change: acc.change + (now - then), base: acc.base + then, n: acc.n + 1 };
    }, { change: 0, base: 0, n: 0 });

    return {
      value, drift,
      driftPct: drift.base > 0 ? (drift.change / drift.base) * 100 : null,
      people:  people.size,
      overdue: open.filter(e => isOverdue(e.lend)).length,
      back:    open.filter(e => e.back).length,
      longest: open.reduce((m, e) => Math.max(m, daysOut(e.lend)), 0),
    };
  }, [open, livePrices, archive]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = e =>
      !q ||
      e.row.skin?.value?.toLowerCase().includes(q) ||
      e.lend.to.name.toLowerCase().includes(q);
    if (filter === 'returned') return closed.filter(match);
    if (filter === 'overdue')  return open.filter(e => isOverdue(e.lend)).filter(match);
    return open.filter(match);
  }, [open, closed, filter, query]);

  if (!open.length && !closed.length) {
    return (
      <div className="zn-root">
        <ZoneHead pre="THE" hl="LOAN" post="BOARD" sub="Items that are out with friends but still yours." />
        <ZoneEmpty>
          Nothing is out on loan.
          <div style={{ marginTop: 16 }}>
            <button className="zn-btn primary" onClick={() => onLendItems && onLendItems()}>Lend an item</button>
          </div>
          <p style={{ maxWidth: 460, margin: '18px auto 0', fontSize: 15, fontWeight: 700, lineHeight: 1.6 }}>
            A lend keeps the item in your totals. The cost basis stays on the row and the market
            value still counts toward your portfolio. Only its whereabouts change.
          </p>
        </ZoneEmpty>
      </div>
    );
  }

  return (
    <div className="zn-root">

      <ZoneHead
        pre="THE" hl="LOAN" post="BOARD"
        sub="Who has what, since when, and what it is worth while it is gone."
        stickers={[
          { text: `${open.length} out on loan`, bg: ZONE.teal },
          totals.overdue > 0 && { text: `${totals.overdue} overdue`, bg: ZONE.hot },
          totals.back > 0 && { text: `${totals.back} back in your locker`, bg: ZONE.lime },
        ]}
      />

      <ZoneStats items={[
        { label: 'Value on loan', value: `$${fmt(totals.value)}`,
          sub: totals.drift.n > 0
            ? `${totals.drift.change >= 0 ? '+' : '-'}$${fmt(Math.abs(totals.drift.change))}${
                totals.driftPct != null ? ` (${totals.driftPct >= 0 ? '+' : ''}${totals.driftPct.toFixed(1)}%)` : ''
              } since they went out`
            : 'still counted in your portfolio', bg: ZONE.teal },
        { label: 'Items out', value: String(open.length),
          sub: `with ${totals.people} ${totals.people === 1 ? 'person' : 'people'}`, bg: ZONE.white },
        { label: 'Longest loan', value: `${totals.longest}d`,
          sub: totals.overdue > 0 ? `${totals.overdue} past their date` : 'none overdue',
          bg: totals.overdue > 0 ? ZONE.orange : ZONE.yellow },
      ]} />

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="zn-bar">
        <ZoneSearch value={query} onChange={setQuery} placeholder="Search item or friend…" />
        <ZoneChips options={FILTERS} value={filter} onChange={setFilter} />
        <span className="zn-bar-actions" style={{ marginLeft: 'auto' }}>
          <button className="zn-btn primary" onClick={() => onLendItems && onLendItems()}>Lend an item</button>
        </span>
      </div>

      {/* ── Loans ──────────────────────────────────── */}
      {visible.length === 0 ? (
        <ZoneEmpty>
          {filter === 'returned' ? 'Nothing has come back yet.'
            : filter === 'overdue' ? 'Nothing is overdue.'
            : 'No loans match your search.'}
        </ZoneEmpty>
      ) : visible.map((e, i) => {
        const img  = getRowImage(e.row, skinsData, inventoryMap);
        const mkt  = getBuffPrice(livePrices, e.row);
        const cost = parseFloat(e.row.buyPrice) || 0;
        // Market drift over the life of the loan. Not profit: the item is still
        // yours and still in your totals. It answers "is it moving while I am
        // not holding it?".
        // Priced off the CSFloat archive on the day it left when the archive
        // knows the item, off the row's own snapshots when it does not.
        const mv   = archive.get(lendKey(e));
        const then = mv ? mv.then : lentValue(e.row, e.lend);
        const nowV = mv ? mv.now : mkt;
        const move = then != null && nowV != null ? nowV - then : null;
        const movePct = move != null && then > 0 ? (move / then) * 100 : null;
        const done = filter === 'returned';
        const over = !done && isOverdue(e.lend);
        const due  = daysUntilDue(e.lend);
        return (
          <motion.div key={e.lend.id || `${e.realIndex}_${i}`}
            className={`zn-lend${over ? ' over' : ''}${done ? ' done' : ''}`} {...drop(i + 3, 0)}>

            <div className="zn-lend-item idm-clickable"
              role="button" tabIndex={0} title="Open details"
              onClick={() => setDetail({ row: e.row, img, lend: e.lend, then })}
              onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setDetail({ row: e.row, img, lend: e.lend, then }); } }}>
              {img ? <img src={img} alt="" /> : <div className="zn-thumb-blank" style={{ width: 62, height: 44 }} />}
              <div style={{ minWidth: 0 }}>
                <div className="zn-trade-name">{e.row.skin?.value}</div>
                <div className="zn-trade-sub">
                  {e.row.condition?.value || 'n/a'} · cost ${fmt(cost)}
                  {mkt != null ? ` · worth $${fmt(mkt)}` : ''}
                </div>
                {move != null && (
                  <div className="zn-lend-move" style={{ background: move >= 0 ? ZONE.lime : ZONE.hot }}>
                    {move >= 0 ? '+' : '-'}${fmt(Math.abs(move))}
                    {movePct != null ? ` (${movePct >= 0 ? '+' : ''}${movePct.toFixed(1)}%)` : ''}
                    {done ? ' while it was out' : ' since lent'}
                  </div>
                )}
                {/* Which day the "was worth" actually came from. Before mid-2025
                    the archive only has the days a skin sold, so the baseline is
                    often the nearest one rather than the exact date. */}
                {mv && (
                  <div className="zn-trade-sub" style={{ opacity: 0.7 }}>{readingNote(mv)}</div>
                )}
                {/* Drawn on every card, empty or not, so a crafted item does not
                    stand taller than the plain one under it. */}
                <StickerSlots
                  stickers={withStickerImages(e.row.stickers, stickerIndex)}
                  charms={e.row.charms}
                  prices={livePrices} variant="zone"
                  hideWhenEmpty={false}
                  blank={!(e.row.stickers || []).some(s => s?.name)}
                  style={{ marginTop: 7 }} />
              </div>
            </div>

            <div className="zn-lend-who">
              <div className="k">{done ? 'Was with' : 'With'}</div>
              <div className="v" title={e.lend.to.name}>
                {e.lend.to.profileUrl
                  ? <a href={e.lend.to.profileUrl} target="_blank" rel="noreferrer">{e.lend.to.name}</a>
                  : e.lend.to.name}
              </div>
              <div className="s">
                {done
                  ? e.lend.writtenOffAt
                    ? `written off ${fmtDate(e.lend.writtenOffAt)}`
                    : `returned ${fmtDate(e.lend.returnedAt)}`
                  : `since ${fmtDate(e.lend.at)} · ${daysOut(e.lend)}d out`}
              </div>
              {e.lend.note && <div className="zn-lend-note" title={e.lend.note}>{e.lend.note}</div>}
            </div>

            {!done && (
              <div className="zn-lend-act">
                <div className="zn-lend-flags">
                  {/* The flag cell is narrow enough that the longer wordings
                      ellipsize, so each one also carries its full text as a
                      tooltip. */}
                  {e.back && (
                    <span className="zn-lend-flag" style={{ background: e.back.certain ? ZONE.lime : ZONE.yellow }}
                      title={e.back.certain
                        ? 'This item is back in your locker — a float match confirms it.'
                        : 'An item matching this one is in your locker, but the float does not confirm it.'}>
                      {e.back.certain ? 'back in your locker' : 'looks like it is back'}
                    </span>
                  )}
                  {over && <span className="zn-lend-flag" style={{ background: ZONE.hot }} title={`${Math.abs(due)} days overdue`}>{Math.abs(due)}d overdue</span>}
                  {!over && due != null && <span className="zn-lend-flag" style={{ background: ZONE.white }} title={`Due back in ${due} days`}>due in {due}d</span>}
                </div>
                <button className="zn-btn primary"
                  onClick={() => onMarkReturned && onMarkReturned(e.realIndex, e.back?.item?.assetId || null)}>
                  Mark returned
                </button>
                <button className="zn-btn" onClick={() => onWriteOff && onWriteOff(e.realIndex)}>Write off</button>
              </div>
            )}
          </motion.div>
        );
      })}

      <ZoneFooter stickers={[
        { text: 'a lend never touches your profit', bg: ZONE.purple },
        { text: `$${fmt(totals.value)} out on loan`, bg: ZONE.teal, right: true },
      ]} />

      {detail && (
        <ItemDetailModal
          stickerIndex={stickerIndex}
          variant="zone"
          kind="lend"
          name={detail.row.skin?.value}
          condition={detail.row.condition?.value}
          image={detail.img}
          history={detail.row.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          row={detail.row}
          lend={detail.lend}
          lentPrice={detail.then}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
